/**
 * Loop keys and run keys. A loop's key is 2–4 capital letters, unique within its
 * customer (`SC` for "Ship a change"); a run's key is its loop's key and its number
 * within the loop (`SC-52`). The api stores loop keys; run keys are always derived.
 *
 * @since 1.0.0
 */
import { Option, Schema } from "effect";

/**
 * A loop key: 2–4 capital letters.
 *
 * @since 1.0.0
 */
export const LOOP_KEY_PATTERN = /^[A-Z]{2,4}$/;

/**
 * A loop key on the wire.
 *
 * @since 1.0.0
 */
export const LoopKey = Schema.String.pipe(
  Schema.pattern(LOOP_KEY_PATTERN, { message: () => "a loop key is 2 to 4 capital letters, e.g. SC" }),
);

/**
 * @since 1.0.0
 */
export const isLoopKey = (s: string): boolean => LOOP_KEY_PATTERN.test(s);

/** Words that never give a letter to a key. */
const SKIPPED = new Set(["A", "AN", "THE", "OF", "FOR", "TO", "AND", "IN", "ON"]);

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** The name's words in ASCII capitals: accents dropped, bracketed parts removed. */
const wordsOf = (name: string): ReadonlyArray<string> =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\([^)]*\)|\[[^\]]*\]/g, " ")
    .split(/[^A-Za-z]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.toUpperCase());

/** The base, the base grown by the next letters of the word it ended on, then its first 3 letters (at most) plus A–Z. */
const candidatesOf = (base: string, rest: string): ReadonlyArray<string> => [
  base,
  ...Array.from({ length: Math.min(4 - base.length, rest.length) }, (_, n) => base + rest.slice(0, n + 1)),
  ...Array.from(LETTERS, (letter) => base.slice(0, 3) + letter),
];

/** The i-th 4-letter key in alphabetical order: 0 → `AAAA`, 1 → `AAAB`. */
const fourLetterKeyAt = (i: number): string =>
  Array.from({ length: 4 }, (_, p) => LETTERS[Math.floor(i / 26 ** (3 - p)) % 26]).join("");

/**
 * The key a loop gets from its name when none is chosen: the initials of the name's main
 * words, at most 4 ("Ship a change (fm)" → `SC`); a one-word name gives its first 3
 * letters ("Research" → `RES`). Skipped: a, an, the, of, for, to, and, in, on, and
 * anything in brackets. A name with no letters is keyed as if named "Loop" (`LOO`); a
 * one-letter base is padded with `X`. When the base is in `taken`, it grows by the next
 * letters of the word it ended on ("Ship a change" with `SC` taken → `SCH`, then `SCHA`),
 * then tries its first 3 letters plus A–Z, then every 4-letter key from `AAAA` — so the
 * result is never in `taken`.
 *
 * @since 1.0.0
 */
export const initialsKey = (name: string, taken: ReadonlySet<string>): string => {
  const words = wordsOf(name);
  const main = words.filter((word) => !SKIPPED.has(word));
  const used = main.length > 0 ? main : words.length > 0 ? words : ["LOOP"];
  const single = used.length === 1;
  const last = used[Math.min(used.length, 4) - 1]!;
  const base = (single ? last.slice(0, 3) : used.slice(0, 4).map((word) => word[0]).join("")).padEnd(2, "X");
  const found = candidatesOf(base, last.slice(single ? 3 : 1)).find((key) => !taken.has(key));
  if (found !== undefined) return found;
  let i = 0;
  while (taken.has(fourLetterKeyAt(i))) i++;
  return fourLetterKeyAt(i);
};

/**
 * A run's key: `SC-52`.
 *
 * @since 1.0.0
 */
export const runKeyOf = (loopKey: string, number: number): string => `${loopKey}-${number}`;

const RUN_KEY = /^([A-Za-z]{2,4})-([1-9][0-9]{0,8})$/;

/**
 * Read a run key, case-insensitively (`sc-52` is `SC-52`); none for anything else,
 * including a run id.
 *
 * @since 1.0.0
 */
export const parseRunKey = (s: string): Option.Option<{ readonly loopKey: string; readonly number: number }> =>
  Option.fromNullable(RUN_KEY.exec(s.trim())).pipe(
    Option.map((match) => ({ loopKey: match[1]!.toUpperCase(), number: Number(match[2]) })),
  );
